import { Link, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useQueryClient } from '@tanstack/react-query';
import { config } from '../config';
import { useAuthRoles } from '../auth/AuthContext';
import { LoginPage } from '../features/auth/LoginPage';
import { MapsListPage } from '../features/maps/MapsListPage';
import { MapPage } from '../features/maps/MapPage';
import { ImportPage } from '../features/imports/ImportPage';
import { AdminMapsPage } from '../features/admin/AdminMapsPage';
import { AdminMapPage } from '../features/admin/AdminMapPage';
import { AdminCreateMapPage } from '../features/admin/AdminCreateMapPage';
import { isAdmin } from '@grs-atlas/shared';

function RequireAuth({ children }: { children: React.ReactNode }) {
  if (config.devAuth) {
    if (!localStorage.getItem('atlas-dev-user')) {
      return <Navigate to="/login" replace />;
    }
    return children;
  }
  return <RequireMsalAuth>{children}</RequireMsalAuth>;
}

function RequireMsalAuth({ children }: { children: React.ReactNode }) {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // useIsAuthenticated is false during Startup; don't bounce to /login until MSAL is ready.
  if (
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect
  ) {
    return <div className="empty">Signing you in…</div>;
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function Shell() {
  const roles = useAuthRoles();
  const email = config.devAuth
    ? localStorage.getItem('atlas-dev-user') ?? 'dev@guidedreach.com'
    : null;

  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to="/maps" className="brand">
          <img src="/grs-atlas-logo.png" alt="GRS Atlas" className="brand-logo" />
        </Link>
        <nav>
          <Link to="/maps">Maps</Link>
          {isAdmin(roles) && <Link to="/admin/maps">Admin</Link>}
        </nav>
        <div className="user">
          {config.devAuth ? (
            email
          ) : (
            <MsalUserMenu />
          )}
        </div>
      </header>
      <main>
        <Outlet />
      </main>
    </div>
  );
}

function MsalUserMenu() {
  const { instance, accounts } = useMsal();
  const queryClient = useQueryClient();
  const account = accounts[0];

  return (
    <>
      {account?.username}
      <button
        type="button"
        className="btn ghost"
        style={{ marginLeft: 8 }}
        onClick={() => {
          void (async () => {
            queryClient.clear();
            // Local logout only: clear app tokens/accounts and emit MSAL events so
            // msal-react updates, but skip Entra end_session (keeps SSO).
            await instance.logoutRedirect({
              account,
              onRedirectNavigate: () => false,
            });
            window.location.assign('/login');
          })();
        }}
      >
        Sign out
      </button>
    </>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/maps" replace />} />
        <Route path="/maps" element={<MapsListPage />} />
        <Route path="/maps/:mapId" element={<MapPage />} />
        <Route path="/maps/:mapId/import" element={<ImportPage />} />
        <Route path="/admin/maps" element={<AdminMapsPage />} />
        <Route path="/admin/maps/new" element={<AdminCreateMapPage />} />
        <Route path="/admin/maps/:mapId" element={<AdminMapPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/maps" replace />} />
    </Routes>
  );
}
